import {
  DEFAULT_SHIP_ID,
  DEFAULT_WEAPON_ID,
  type ShipDef,
  type WeaponDef,
} from '../sim/defs';
import type { SecondaryDef } from '../sim/secondary';
import { DEFAULT_SECONDARY_ID } from '../sim/secondary';
import { Stat, type Modifier } from '../sim/stats';
import type { MetaNodeDef } from './meta';

/* ═══════════════════════════════════════════════════════════════════════════
   L'ÉQUIPEMENT DISPONIBLE.

   Ce module répond à une seule question, pour trois familles de contenu :
   quelle coque, quelle arme primaire et quelle arme secondaire le joueur
   peut-il emporter ?

   ⚠️ Il existe parce que la réponse était nulle part. L'arbre de
   méta-progression vend cinq armes secondaires, trois vaisseaux et deux armes
   primaires — entre 300 et 3 200 crédits pièce — et rien ne les rendait
   équipables : `World.reset()` posait la Lance perforante en dur et n'en
   changeait jamais, tandis que `grants: 'aegis'` et `grants: 'plasma'`
   désignaient du contenu qui n'existait pas. Le joueur pouvait dépenser
   plusieurs milliers de crédits pour des lignes affichées « ACQUIS » qui ne
   changeaient rien.

   C'est la même famille de panne que les drones invisibles, le curseur de flash
   inopérant et `maxLives` que rien ne lisait : chaque couche fonctionne, la
   soudure manque, et rien ne le signale. D'où les fonctions de vérification en
   bas de fichier, appelées par les tests — un déblocage qui ne désigne aucun
   contenu réel doit être une erreur bruyante, pas un bouton sans effet.

   ── LA FORME COMMUNE AUX TROIS FAMILLES ────────────────────────────────────

   Les trois familles ont exactement les mêmes trois besoins, et ce n'est pas
   une coïncidence : ce sont les trois questions que pose n'importe quel
   catalogue accroché à un arbre d'achats.

     1. ce que le joueur peut emporter        → `debloque`
     2. ce qu'il emporte réellement, le choix
        mémorisé pouvant être périmé          → `resout`
     3. ce que l'arbre n'ouvre à personne     → `inatteignables`

   Le noyau générique est écrit une fois, et chaque famille l'expose sous son
   propre nom avec son propre contenu de série. Trois copies auraient divergé —
   la première à sortir du lot aurait été le repli, celui qu'on ne teste que
   lorsqu'un joueur signale le bug.
   ═══════════════════════════════════════════════════════════════════════════ */

/** Le seul point commun exigé d'un catalogue : ses entrées ont un identifiant
 *  textuel, et c'est cet identifiant que `grants` désigne. */
interface Catalogable {
  readonly id: string;
}

/** Contenu emportable : ce que les nœuds achetés accordent, plus le contenu de
 *  série qui est TOUJOURS disponible.
 *
 *  ⚠️ Le contenu de série est offert même si aucun nœud ne l'accorde. Sans
 *  cela, un joueur sans achat n'aurait ni coque, ni arme, ni secondaire : la
 *  run démarrerait sur un profil vide et le clic droit resterait muet, ce dont
 *  il conclurait — à juste titre — que le jeu est cassé. */
function debloque<T extends Catalogable>(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
  catalogue: readonly T[],
  serie: string,
): T[] {
  const accordes = new Set<string>([serie]);
  for (const n of nodes) {
    if (n.kind !== 'option' || !bought.has(n.id)) continue;
    accordes.add(n.grants);
  }
  return catalogue.filter((c) => accordes.has(c.id));
}

/** Index dans le catalogue COMPLET du contenu choisi, avec repli.
 *
 *  ⚠️ Le repli n'est pas de la paranoïa. Une sauvegarde peut désigner un
 *  contenu retiré du catalogue, ou accordé par un nœud depuis remboursé par un
 *  respec. Sans repli, la résolution rendrait −1, et la panne serait silencieuse
 *  et parfaitement inexplicable pour le joueur : une arme secondaire qui ne tire
 *  pas, ou pire, un profil de statistiques absent — donc une run entière jouée
 *  sur des chiffres qui ne sont pas ceux du hangar.
 *
 *  L'index est rendu sur le catalogue complet et non sur la liste des
 *  disponibles : c'est le catalogue qui fait foi côté simulation, la liste des
 *  disponibles ne servant qu'à décider ce qui est LÉGITIME. */
function resout<T extends Catalogable>(
  choisi: string,
  disponibles: readonly T[],
  catalogue: readonly T[],
  serie: string,
): number {
  const ok = disponibles.some((c) => c.id === choisi);
  const id = ok ? choisi : (disponibles[0]?.id ?? serie);
  return catalogue.findIndex((c) => c.id === id);
}

/** Contenu du catalogue qu'AUCUN nœud n'ouvre.
 *
 *  ⚠️ La vérification va dans ce sens-là, et pas dans l'autre. La première
 *  version listait les nœuds d'arme dont le `grants` ne désigne aucune
 *  secondaire — et elle signalait `arme-lance-plasma` et `arme-faisceau-continu`,
 *  qui étaient parfaitement corrects : `OptionKind.Weapon` couvre aussi les
 *  armes PRIMAIRES, qui n'existaient pas encore. Le test avait tort, pas le
 *  contenu. (Elles existent désormais, mais l'argument tient toujours : un
 *  `grants` en avance sur son contenu est une intention, pas un défaut.)
 *
 *  Le sens utile est celui-ci : un contenu écrit, équilibré, testé et livré que
 *  rien ne débloque est invisible à jamais. C'est la seule des deux erreurs qui
 *  coûte quelque chose au joueur, et la seule que l'on puisse détecter sans
 *  supposer une convention de nommage.
 *
 *  Le contenu de série est exclu : il est offert, donc n'a pas à être accordé
 *  par un nœud. */
function inatteignables<T extends Catalogable>(
  nodes: readonly MetaNodeDef[],
  catalogue: readonly T[],
  serie: string,
): string[] {
  const accordes = new Set<string>([serie]);
  for (const n of nodes) if (n.kind === 'option') accordes.add(n.grants);
  return catalogue.filter((c) => !accordes.has(c.id)).map((c) => c.id);
}

/* ── Armes secondaires ────────────────────────────────────────────────────── */

/** Armes secondaires que le joueur peut emporter, dans l'ordre du catalogue. */
export function unlockedSecondaries(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
  secondaries: readonly SecondaryDef[],
): SecondaryDef[] {
  return debloque(nodes, bought, secondaries, DEFAULT_SECONDARY_ID);
}

/** Index dans `secondaries` de l'arme choisie, avec repli sur la Lance. */
export function resolveSecondary(
  choisi: string,
  disponibles: readonly SecondaryDef[],
  secondaries: readonly SecondaryDef[],
): number {
  return resout(choisi, disponibles, secondaries, DEFAULT_SECONDARY_ID);
}

/** Armes secondaires du catalogue qu'AUCUN nœud n'ouvre. */
export function unreachableSecondaries(
  nodes: readonly MetaNodeDef[],
  secondaries: readonly SecondaryDef[],
): string[] {
  return inatteignables(nodes, secondaries, DEFAULT_SECONDARY_ID);
}

/* ── Vaisseaux ────────────────────────────────────────────────────────────── */

/** Coques pilotables, dans l'ordre du catalogue. La coque de série y est
 *  toujours, et elle doit y rester : le plan (§6.9) exige que le jeu soit
 *  franchissable à 100 % sans le moindre achat. */
export function unlockedShips(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
  ships: readonly ShipDef[],
): ShipDef[] {
  return debloque(nodes, bought, ships, DEFAULT_SHIP_ID);
}

/** Index dans `ships` de la coque choisie, avec repli sur celle de série. */
export function resolveShip(
  choisi: string,
  disponibles: readonly ShipDef[],
  ships: readonly ShipDef[],
): number {
  return resout(choisi, disponibles, ships, DEFAULT_SHIP_ID);
}

/** Vaisseaux du catalogue qu'AUCUN nœud n'ouvre. */
export function unreachableShips(
  nodes: readonly MetaNodeDef[],
  ships: readonly ShipDef[],
): string[] {
  return inatteignables(nodes, ships, DEFAULT_SHIP_ID);
}

/* ── Armes primaires ──────────────────────────────────────────────────────── */

/** Armes primaires emportables, dans l'ordre du catalogue. */
export function unlockedWeapons(
  nodes: readonly MetaNodeDef[],
  bought: ReadonlySet<string>,
  weapons: readonly WeaponDef[],
): WeaponDef[] {
  return debloque(nodes, bought, weapons, DEFAULT_WEAPON_ID);
}

/** Index dans `weapons` de l'arme choisie, avec repli sur le VULCAN. */
export function resolveWeapon(
  choisi: string,
  disponibles: readonly WeaponDef[],
  weapons: readonly WeaponDef[],
): number {
  return resout(choisi, disponibles, weapons, DEFAULT_WEAPON_ID);
}

/** Armes primaires du catalogue qu'AUCUN nœud n'ouvre. */
export function unreachableWeapons(
  nodes: readonly MetaNodeDef[],
  weapons: readonly WeaponDef[],
): string[] {
  return inatteignables(nodes, weapons, DEFAULT_WEAPON_ID);
}

/* ── Le profil emporté en run ─────────────────────────────────────────────── */

/**
 * Modificateurs apportés par la coque et l'arme choisies.
 *
 * À semer dans `world.progress.baseMods` APRÈS `reset()` et AVANT `syncStats()`,
 * au même endroit et de la même façon que `metaModifiers` — les trois sources
 * empruntent le chemin des cartes ramassées, ce qui est la seule garantie qu'un
 * profil de vaisseau ne se composera jamais autrement qu'une amélioration.
 *
 * ⚠️ L'ordre d'empilement ne change pas le RÉSULTAT : `computeStats` applique
 * tous les additifs avant tous les multiplicatifs, donc empiler méta, vaisseau
 * et arme dans un sens ou dans l'autre donne les mêmes statistiques. Il n'y a
 * pas de « bon ordre » à retenir, donc pas d'ordre à se tromper.
 *
 * ⚠️ Mais il le change au DERNIER BIT. La multiplication flottante n'est pas
 * associative : `(v × 1,08) × 0,75` et `(v × 0,75) × 1,08` diffèrent d'un ulp,
 * et l'écart se propage dans toutes les statistiques dérivées. Sans conséquence
 * sur l'équilibrage — sur l'empreinte de replay, si. Le câblage doit donc
 * empiler les trois sources dans un ordre FIXE, quel qu'il soit, et ne plus en
 * changer : deux versions du jeu qui empilent différemment produisent des
 * replays qui divergent sans qu'aucune valeur de jeu n'ait bougé.
 *
 * Les deux arguments sont facultatifs pour un motif précis : `resolveShip` et
 * `resolveWeapon` rendent un INDEX, et un index se lit dans un tableau, qui peut
 * rendre `undefined`. Refuser silencieusement ce cas donnerait une run jouée sur
 * un profil vide sans que rien ne le dise ; l'accepter explicitement fait au
 * moins retomber le joueur sur les statistiques de série, qui sont exactement
 * celles du contenu de départ.
 */
export function loadoutModifiers(
  ship: ShipDef | undefined,
  weapon: WeaponDef | undefined,
): Modifier[] {
  const out: Modifier[] = [];
  if (ship) {
    out.push(...ship.modifiers);
    /* ⚠️ LA soudure enfin posée. `ShipDef.secondaryCooldownMul` était une
       promesse écrite (le FANAL vendait « recharge ×0,5 ») que RIEN ne lisait —
       la cinquième occurrence de la panne dans ce projet. On la traduit ici en
       modificateur de la statistique `secondaryCooldownMul`, exactement comme
       `ship.modifiers` : le profil du vaisseau devient des cartes, et
       `computeStats` le compose avec les cartes de recharge du pool.

       Conversion : le champ est un FACTEUR (0,5 = moitié), la statistique de
       base vaut 1, et un `mul` applique `1 × (1 + value)`. Il faut donc
       `value = secondaryCooldownMul − 1` (0,5 → −0,5). On ne pousse rien quand
       le facteur vaut 1 : sans cela, la coque de série injecterait un `mul 0`
       inerte et « profil de série = [] » deviendrait faux. */
    if (ship.secondaryCooldownMul !== 1) {
      out.push({
        stat: Stat.SecondaryCooldownMul,
        op: 'mul',
        value: ship.secondaryCooldownMul - 1,
      });
    }
  }
  if (weapon) out.push(...weapon.modifiers);
  return out;
}
